import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  UserRound, BriefcaseBusiness, Landmark, FileText, Loader2, Upload, Trash2,
  Pencil, Eye, EyeOff, Lock, ShieldCheck, AlertCircle, Clock3, KeyRound, Check, LogOut,
} from 'lucide-react'
import { apiGet, cachedGet, patchJson, postJson } from '../../lib/api.js'
import { useMe } from '../../lib/me.js'
import { generateThumbnail, blobToDataURL } from '../../lib/thumbnail.js'
import { uploadFile } from '../../lib/uploadDirect.js'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'
import ResignBlock from '../../components/me/ResignBlock.jsx'
import {
  BANKS, MY_FILE_TAGS, fmtDateTH, fmtDateTimeTH, fmtBytes, fmtBankNum, maskTail,
  relabelBranch, isImageFile, fileTagText, fileHref,
  canDeleteFile, deleteWindowLeft, fmtWindowLeft,
} from '../../lib/meProfile.js'

const INPUT = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 placeholder:text-slate-400 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

/**
 * ตั้งค่า — the staff member's own record.
 *
 * Almost everything here is READ-ONLY on purpose, and the page says so rather
 * than leaving it to be discovered: name, position, role, branch and the
 * employment dates drive payroll and access, so they stay HR's to set. The two
 * things a person genuinely owns — where their salary is paid, their own login
 * password, and the documents they file about themselves — are what this page
 * lets them change.
 */
export default function MeSetting() {
  const me = useMe()
  const [rec, setRec] = useState(null)
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    const r = await apiGet('/api/me/profile')
    setRec(r)
    return r
  }, [])

  useEffect(() => {
    let alive = true
    setLoading(true)
    Promise.all([
      load().catch((e) => { if (alive) setErr(String(e.message || e)); return null }),
      cachedGet('/api/branches').catch(() => []),
    ])
      .then(([, bs]) => {
        if (!alive) return
        setBranches(Array.isArray(bs) ? bs : (Array.isArray(bs?.items) ? bs.items : []))
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [load])

  const branchName = useMemo(() => {
    const m = new Map()
    for (const b of branches) m.set(Number(b.id), relabelBranch(b.name))
    return (id) => m.get(Number(id)) || (Number(id) ? `สาขา #${id}` : '—')
  }, [branches])

  if (loading) {
    return <div className="flex items-center gap-2 text-[14px] text-slate-400 py-10"><Loader2 size={16} className="animate-spin" /> กำลังโหลด…</div>
  }
  if (err && !rec) {
    return (
      <div className="max-w-[560px] rounded-2xl border border-rose-200 bg-rose-50/60 px-5 py-4 text-[13.5px] text-rose-700">
        โหลดข้อมูลไม่สำเร็จ — {err}
      </div>
    )
  }

  return (
    <div className="max-w-[860px] space-y-6">
      <PersonalCard rec={rec} />
      <EmploymentCard rec={rec} branchName={branchName} />
      <BankCard rec={rec} onSaved={setRec} />
      <SecurityCard />
      <FilesCard rec={rec} me={me} onChanged={setRec} />
      {/* การลาออก sits LAST, and in ตั้งค่า rather than in its own tab: it is a
          once-in-a-career act that belongs beside the employment facts it changes
          (EmploymentCard above holds วันเริ่มงาน / วันสิ้นสุดการจ้าง), not in a nav row
          people read daily. It also keeps ลาออก far away from ขอลา — one syllable
          apart in Thai, and one of them is used constantly. */}
      <Card
        icon={LogOut}
        title="การลาออก"
        note="แจ้งความประสงค์ลาออก และแบบสอบถามก่อนพ้นสภาพ"
      >
        <ResignBlock />
      </Card>
    </div>
  )
}

/* ── shared shell ─────────────────────────────────────────────────────────── */

function Card({ icon: Icon, title, note, right, children }) {
  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white">
      <header className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100">
        <Icon size={15} className="text-brand-600 flex-none" strokeWidth={1.9} />
        <h2 className="text-[14px] font-semibold text-slate-800">{title}</h2>
        {note && <span className="text-[11.5px] text-slate-400 truncate">{note}</span>}
        {right && <div className="ml-auto flex-none">{right}</div>}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  )
}

// Read-only fields are rendered as text, not as disabled inputs: a greyed-out
// box invites clicking and reads as "broken", while a definition list reads as
// "this is a record".
function Row({ label, children, wide }) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <dt className="text-[12px] font-medium text-slate-400 mb-0.5">{label}</dt>
      <dd className="text-[13.5px] text-slate-800 break-words">{children || '—'}</dd>
    </div>
  )
}

const Grid = ({ children }) => (
  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3.5">{children}</dl>
)

const LockNote = ({ children }) => (
  <p className="flex items-start gap-1.5 text-[12px] text-slate-400 mt-4 pt-3 border-t border-slate-100">
    <Lock size={12} className="mt-[2px] flex-none" strokeWidth={1.9} />
    <span>{children}</span>
  </p>
)

/* ── ข้อมูลส่วนตัว ─────────────────────────────────────────────────────────── */

function PersonalCard({ rec }) {
  const [showId, setShowId] = useState(false)
  const idcard = String(rec?.idcardnumber || '')
  return (
    <Card icon={UserRound} title="ข้อมูลส่วนตัว">
      <Grid>
        <Row label="ชื่อ-นามสกุล">{[rec?.prefix, rec?.name].filter(Boolean).join('')}</Row>
        <Row label="ชื่อเล่น">{rec?.nickname}</Row>
        <Row label="ชื่อ (อังกฤษ)">{rec?.name_eng}</Row>
        <Row label="รหัสพนักงาน">{rec?.code}</Row>
        <Row label="อีเมล">{rec?.email}</Row>
        <Row label="เบอร์โทร">{rec?.phone}</Row>
        <Row label="เลขบัตรประชาชน">
          {idcard ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="tabular-nums">{showId ? idcard : maskTail(idcard)}</span>
              <button
                type="button"
                onClick={() => setShowId((v) => !v)}
                title={showId ? 'ซ่อน' : 'แสดง'}
                className="p-0.5 text-slate-300 hover:text-slate-600"
              >
                {showId ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
            </span>
          ) : null}
        </Row>
        <Row label="ที่อยู่" wide>{rec?.address}</Row>
      </Grid>
      <LockNote>
        ข้อมูลส่วนตัวแก้ไขได้โดยฝ่ายบุคคลเท่านั้น — หากไม่ถูกต้อง กรุณาแจ้ง HR
      </LockNote>
    </Card>
  )
}

/* ── การจ้างงาน ───────────────────────────────────────────────────────────── */

function EmploymentCard({ rec, branchName }) {
  const roles = Array.isArray(rec?.role) ? rec.role : (rec?.role ? [rec.role] : [])
  return (
    <Card
      icon={BriefcaseBusiness}
      title="การจ้างงาน"
      right={
        <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11.5px] font-medium ring-1 ${
          rec?.active !== false
            ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
            : 'bg-slate-100 text-slate-500 ring-slate-200'
        }`}>
          <ShieldCheck size={12} strokeWidth={2} />
          {rec?.active !== false ? 'ใช้งาน' : 'ปิดใช้งาน'}
        </span>
      }
    >
      <Grid>
        <Row label="ตำแหน่ง">{rec?.position}</Row>
        <Row label="สาขาที่ทำงาน">{branchName(rec?.branch_id)}</Row>
        <Row label="วันเริ่มงาน">{rec?.date_start ? fmtDateTH(rec.date_start) : '—'}</Row>
        <Row label="วันผ่านทดลองงาน">{rec?.date_probationpass ? fmtDateTH(rec.date_probationpass) : '—'}</Row>
        <Row label="โควตาการลา">{Number(rec?.leavequota || 0) ? `${rec.leavequota} วัน/ปี` : '—'}</Row>
        <Row label="ประกันสังคม">{rec?.sso ? 'ส่ง สปส.' : 'ไม่ส่ง สปส.'}</Row>
        <Row label="สิทธิ์การใช้งาน" wide>
          {roles.length ? (
            <span className="flex flex-wrap gap-1.5">
              {roles.map((r) => (
                <span key={r} className="inline-flex items-center rounded-lg bg-slate-100 text-slate-600 px-2 py-0.5 text-[12px] font-medium">{r}</span>
              ))}
            </span>
          ) : null}
        </Row>
      </Grid>
      <LockNote>
        ตำแหน่ง สาขา สิทธิ์การใช้งาน และวันที่จ้างงาน กำหนดโดยฝ่ายบุคคล
      </LockNote>
    </Card>
  )
}

/* ── บัญชีธนาคาร (the one editable block) ─────────────────────────────────── */

function BankCard({ rec, onSaved }) {
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ bank: '', bank_num: '', bank_branchcode: '' })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [ok, setOk] = useState(false)

  function start() {
    setForm({
      bank: rec?.bank || '',
      bank_num: rec?.bank_num || '',
      bank_branchcode: rec?.bank_branchcode || '',
    })
    setErr(''); setOk(false); setEditing(true)
  }

  async function save() {
    const num = form.bank_num.replace(/\D+/g, '')
    // Bank details are what payroll pays into. A half-filled account is worse
    // than none — it looks set but the transfer bounces — so both parts are
    // required together, or the whole block is cleared.
    if (form.bank && !num) { setErr('กรุณากรอกเลขที่บัญชี'); return }
    if (num && !form.bank) { setErr('กรุณาเลือกธนาคาร'); return }
    if (num && (num.length < 8 || num.length > 15)) { setErr('เลขที่บัญชีไม่ถูกต้อง'); return }
    setSaving(true); setErr('')
    try {
      const next = await patchJson('/api/me/profile', {
        bank: form.bank,
        bank_num: num,
        bank_branchcode: form.bank_branchcode.replace(/\D+/g, ''),
      })
      onSaved?.((cur) => ({ ...cur, ...next }))
      setEditing(false); setOk(true)
    } catch (e) {
      setErr(readableError(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      icon={Landmark}
      title="บัญชีธนาคาร"
      note="สำหรับโอนเงินเดือน"
      right={!editing && (
        <button
          onClick={start}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-[12.5px] font-medium text-slate-600 hover:border-brand-300 hover:text-brand-700"
        >
          <Pencil size={12} /> แก้ไข
        </button>
      )}
    >
      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[12px] font-medium text-slate-400 mb-1">ธนาคาร</label>
              <select value={form.bank} onChange={(e) => setForm((f) => ({ ...f, bank: e.target.value }))} className={INPUT}>
                <option value="">— เลือกธนาคาร —</option>
                {BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-medium text-slate-400 mb-1">เลขที่บัญชี</label>
              <input
                value={form.bank_num}
                onChange={(e) => setForm((f) => ({ ...f, bank_num: e.target.value }))}
                inputMode="numeric"
                placeholder="เช่น 1234567890"
                className={INPUT}
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-slate-400 mb-1">รหัสสาขา</label>
              <input
                value={form.bank_branchcode}
                onChange={(e) => setForm((f) => ({ ...f, bank_branchcode: e.target.value }))}
                inputMode="numeric"
                placeholder="ไม่บังคับ"
                className={INPUT}
              />
            </div>
          </div>
          <p className="flex items-start gap-1.5 text-[12px] text-amber-700 bg-amber-50 rounded-xl px-3 py-2">
            <AlertCircle size={13} className="mt-[2px] flex-none" strokeWidth={1.9} />
            ตรวจสอบให้ตรงกับสมุดบัญชี — เงินเดือนจะโอนเข้าบัญชีนี้
          </p>
          {err && <p className="text-[12.5px] text-rose-600">{err}</p>}
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => setEditing(false)} className="px-3.5 py-2 rounded-xl text-[13px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
            <button
              onClick={save}
              disabled={saving}
              className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {saving && <Loader2 size={13} className="animate-spin" />} บันทึก
            </button>
          </div>
        </div>
      ) : (
        <>
          <Grid>
            <Row label="ธนาคาร">{rec?.bank}</Row>
            <Row label="เลขที่บัญชี">
              {rec?.bank_num ? <span className="tabular-nums">{fmtBankNum(rec.bank_num)}</span> : null}
            </Row>
            <Row label="รหัสสาขา">{rec?.bank_branchcode}</Row>
          </Grid>
          {!rec?.bank_num && (
            <p className="text-[12.5px] text-amber-700 bg-amber-50 rounded-xl px-3 py-2 mt-3">
              ยังไม่ได้ระบุบัญชีธนาคาร — กรุณาเพิ่มเพื่อให้โอนเงินเดือนได้
            </p>
          )}
          {ok && <p className="text-[12.5px] text-emerald-700 mt-3">บันทึกบัญชีธนาคารแล้ว</p>}
        </>
      )}
    </Card>
  )
}

/* ── ความปลอดภัย ──────────────────────────────────────────────────────────── */

// The rules the Xano user.password column enforces (min 8 / minAlpha 1 /
// minDigit 1). Shown live so nobody has to discover them by being rejected —
// but the server checks them too, and it is the one that decides.
const PW_RULES = [
  { text: 'อย่างน้อย 8 ตัวอักษร', ok: (v) => v.length >= 8 },
  { text: 'มีตัวอักษรอย่างน้อย 1 ตัว', ok: (v) => /[A-Za-z]/.test(v) },
  { text: 'มีตัวเลขอย่างน้อย 1 ตัว', ok: (v) => /[0-9]/.test(v) },
]

function PwField({ label, value, onChange, autoComplete, placeholder }) {
  const [show, setShow] = useState(false)
  return (
    <div>
      <label className="block text-[12px] font-medium text-slate-400 mb-1">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          placeholder={placeholder}
          className={`${INPUT} pr-9`}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          title={show ? 'ซ่อน' : 'แสดง'}
          tabIndex={-1}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-300 hover:text-slate-600"
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  )
}

/**
 * รหัสผ่าน — the third thing a person genuinely owns, alongside their bank
 * account and their documents.
 *
 * The current password is required, and it is verified SERVER-SIDE by
 * `api:FeVDPm_m/auth/change-password`. That is the point: holding a session
 * cookie is not the same as knowing the password, so a browser left unlocked at
 * the counter cannot be used to re-key the account.
 *
 * Accounts that only ever sign in with Google have no stored password. There is
 * nothing useful this form can do for them — the server says so in its own words
 * and the message is shown as-is.
 */
function SecurityCard() {
  const EMPTY = { current_password: '', new_password: '', confirm_password: '' }
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const [ok, setOk] = useState(false)

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }))

  function start() {
    setForm(EMPTY); setErr(''); setOk(false); setEditing(true)
  }
  function cancel() {
    setForm(EMPTY); setErr(''); setEditing(false)
  }

  const strong = PW_RULES.every((r) => r.ok(form.new_password))
  const matches = form.new_password !== '' && form.new_password === form.confirm_password
  const canSave = Boolean(form.current_password) && strong && matches && !saving

  async function save() {
    if (!matches) { setErr('รหัสผ่านใหม่ทั้งสองช่องไม่ตรงกัน'); return }
    if (!strong) { setErr('รหัสผ่านใหม่ยังไม่ตรงตามเงื่อนไข'); return }
    setSaving(true); setErr('')
    try {
      await postJson('/api/me/password', form)
      // Clear the fields before anything else — three plaintext passwords should
      // not sit in component state after they have served their purpose.
      setForm(EMPTY); setEditing(false); setOk(true)
    } catch (e) {
      setErr(readableError(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      icon={KeyRound}
      title="รหัสผ่าน"
      note="สำหรับเข้าสู่ระบบด้วยอีเมล"
      right={!editing && (
        <button
          onClick={start}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-[12.5px] font-medium text-slate-600 hover:border-brand-300 hover:text-brand-700"
        >
          <Pencil size={12} /> เปลี่ยนรหัสผ่าน
        </button>
      )}
    >
      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <PwField
              label="รหัสผ่านปัจจุบัน"
              value={form.current_password}
              onChange={set('current_password')}
              autoComplete="current-password"
            />
            <div className="hidden sm:block" />
            <PwField
              label="รหัสผ่านใหม่"
              value={form.new_password}
              onChange={set('new_password')}
              autoComplete="new-password"
            />
            <PwField
              label="ยืนยันรหัสผ่านใหม่"
              value={form.confirm_password}
              onChange={set('confirm_password')}
              autoComplete="new-password"
            />
          </div>

          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[12px]">
            {PW_RULES.map((r) => {
              const pass = r.ok(form.new_password)
              return (
                <li
                  key={r.text}
                  className={`inline-flex items-center gap-1 ${pass ? 'text-emerald-600' : 'text-slate-400'}`}
                >
                  <Check size={12} strokeWidth={pass ? 2.6 : 1.8} /> {r.text}
                </li>
              )
            })}
            {form.confirm_password !== '' && (
              <li className={`inline-flex items-center gap-1 ${matches ? 'text-emerald-600' : 'text-rose-500'}`}>
                <Check size={12} strokeWidth={matches ? 2.6 : 1.8} /> รหัสผ่านใหม่ตรงกัน
              </li>
            )}
          </ul>

          {err && <p className="text-[12.5px] text-rose-600">{err}</p>}

          <div className="flex items-center justify-end gap-2">
            <button onClick={cancel} className="px-3.5 py-2 rounded-xl text-[13px] font-medium text-slate-600 hover:bg-slate-100">ยกเลิก</button>
            <button
              onClick={save}
              disabled={!canSave}
              className="px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {saving && <Loader2 size={13} className="animate-spin" />} เปลี่ยนรหัสผ่าน
            </button>
          </div>
        </div>
      ) : (
        <>
          <Grid>
            <Row label="รหัสผ่าน"><span className="tracking-[0.2em] text-slate-400">••••••••</span></Row>
          </Grid>
          {ok && <p className="text-[12.5px] text-emerald-700 mt-3">เปลี่ยนรหัสผ่านเรียบร้อยแล้ว</p>}
          {/* Said out loud because the opposite is what people assume. Xano auth
              tokens cannot be revoked, so a password change does not sign anyone
              out — if the worry is that someone else got in, changing the
              password alone does not remove them. */}
          <LockNote>
            การเปลี่ยนรหัสผ่านจะไม่ทำให้อุปกรณ์อื่นที่ยังเข้าสู่ระบบอยู่ถูกออกจากระบบ —
            หากสงสัยว่ามีผู้อื่นเข้าใช้บัญชี กรุณาแจ้งผู้ดูแลระบบ
          </LockNote>
        </>
      )}
    </Card>
  )
}

/* ── เอกสารของฉัน ─────────────────────────────────────────────────────────── */

/**
 * Uploads go to Wasabi (`userssdc`) via /api/me/files, which also writes the
 * `file[]` pointer and stamps `created_at` on the server clock.
 *
 * 🔑 The bytes go browser → Wasabi (presign) and only the metadata comes back
 * through /api/me/files?attach=1. This page used to POST the whole file as
 * multipart, which buffered a 25 MB scan three times over in the 128 MB isolate
 * every /api route shares — one of the paths behind the Error 1102 outages. See
 * src/lib/uploadDirect.js, and the contract at the top of functions/api/me/files.js
 * for why this route needs the two-step attach where most routes do not.
 *
 * Deletion is deliberately narrow: the uploader gets 24 hours to take a file
 * back, after that only an Admin can. The countdown is shown on every row that
 * still has one, so nobody has to guess whether the button is about to vanish.
 */
function FilesCard({ rec, me, onChanged }) {
  const files = Array.isArray(rec?.file) ? rec.file : []
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [dragging, setDragging] = useState(false)
  const [progress, setProgress] = useState(null) // { done, total }
  const [tag, setTag] = useState('')
  const [pending, setPending] = useState(null)   // file queued for deletion
  const inputRef = useRef(null)
  const dragDepth = useRef(0)

  // The 24h windows close while the page is open, so the rows re-evaluate on a
  // timer rather than only on load — otherwise a stale delete button would sit
  // there offering something the server now refuses.
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000)
    return () => clearInterval(id)
  }, [])

  const isAdmin = Array.isArray(me?.roles) && me.roles.includes('admin')

  async function onFiles(list) {
    const picked = [...(list || [])]
    if (!picked.length) return
    setBusy(true); setErr(''); setProgress({ done: 0, total: picked.length })
    let failure = ''
    let latest = null
    try {
      for (const file of picked) {
        try {
          // Shrink FIRST so the thumbnail rides along in the same request — the
          // record is then written once, by the server, with the entry complete.
          // Stored inline as a data-URI (the same call the Expense attachments
          // make): a person carries a handful of documents, so a second Wasabi
          // object per image would cost an extra upload, sign and cleanup for no
          // gain. A failed shrink is not fatal — the row falls back to an icon.
          const thumbnail = await generateThumbnail(file).then(blobToDataURL).catch(() => '')
          latest = await uploadOne(file, thumbnail)
        } catch (e2) {
          failure = `${file.name}: ${String(e2.message || e2)}`
          break
        } finally {
          setProgress((p) => (p ? { ...p, done: p.done + 1 } : p))
        }
      }
      if (latest?.file) onChanged?.((cur) => ({ ...cur, file: latest.file }))
      if (failure) setErr(`อัปโหลดไม่สำเร็จ — ${failure}`)
    } catch (e2) {
      setErr(String(e2.message || e2))
    } finally {
      setBusy(false); setProgress(null)
    }
  }

  /**
   * presign → PUT straight to Wasabi → ?attach=1, falling back to the old
   * multipart POST by itself if the bucket's CORS is not set yet (uploadDirect.js).
   *
   * `attach` rather than a plain presign because /api/me/files writes the record
   * itself: `created_at` + `user_id` are what open the 24-hour delete window, so
   * they stay the server's to stamp. tag and thumbnail ride with the attach call
   * only — the presign has no use for them, and an inline data-URI thumbnail is
   * exactly the sort of payload worth keeping out of the Worker twice over.
   */
  async function uploadOne(file, thumbnail) {
    try {
      return await uploadFile('/api/me/files', {}, file, {
        attach: { ...(tag ? { tag } : {}), ...(thumbnail ? { thumbnail } : {}) },
      })
    } catch (e) {
      // Same sentence the old `errorText(r)` produced — uploadFile keeps the
      // parsed refusal body on the Error precisely so this still reads well.
      throw new Error(e?.body ? pickMessage(e.body, e.status) : String(e?.message || e))
    }
  }

  async function remove(f) {
    setPending(null)
    setBusy(true); setErr('')
    try {
      const r = await fetch(`/api/me/files?uuid=${encodeURIComponent(f.uuid)}`, {
        method: 'DELETE', credentials: 'include',
      })
      if (!r.ok) throw new Error(await errorText(r))
      const j = await r.json().catch(() => null)
      onChanged?.((cur) => ({ ...cur, file: j?.file || (cur.file || []).filter((x) => x?.uuid !== f.uuid) }))
    } catch (e) {
      setErr(String(e.message || e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      onDragEnter={(e) => { e.preventDefault(); dragDepth.current += 1; setDragging(true) }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy' }}
      onDragLeave={(e) => { e.preventDefault(); dragDepth.current -= 1; if (dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false) } }}
      onDrop={(e) => { e.preventDefault(); dragDepth.current = 0; setDragging(false); if (!busy) onFiles(e.dataTransfer?.files) }}
    >
      <Card
        icon={FileText}
        title={`เอกสารของฉัน${files.length ? ` (${files.length})` : ''}`}
        right={
          <div className="flex items-center gap-2">
            <select
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              title="ประเภทเอกสารที่จะอัปโหลด"
              className="rounded-lg border border-slate-200 px-2 py-1 text-[12.5px] text-slate-600 outline-none focus:border-brand-400"
            >
              <option value="">— ไม่ระบุประเภท —</option>
              {MY_FILE_TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <button
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-[12.5px] font-medium text-slate-600 hover:border-brand-300 hover:text-brand-700 disabled:opacity-40"
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} อัปโหลด
            </button>
          </div>
        }
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => { const l = e.target.files; e.target.value = ''; onFiles(l) }}
        />

        <div
          onClick={() => !busy && inputRef.current?.click()}
          className={`rounded-xl border border-dashed py-5 text-center transition cursor-pointer ${
            dragging ? 'border-brand-400 bg-brand-50' : 'border-slate-200 hover:border-brand-300 hover:bg-brand-50/30'
          } ${busy ? 'pointer-events-none opacity-70' : ''}`}
        >
          {busy ? (
            <div className="text-[12.5px] text-slate-500">
              <Loader2 size={16} className="inline animate-spin mr-1.5" />
              กำลังอัปโหลด{progress ? ` ${progress.done}/${progress.total}` : ''}…
            </div>
          ) : (
            <div className="text-slate-500">
              <Upload size={18} className={`mx-auto mb-1 ${dragging ? 'text-brand-500' : 'text-slate-400'}`} />
              <div className="text-[12.5px] font-medium">{dragging ? 'วางไฟล์เพื่ออัปโหลด' : 'คลิกหรือลากไฟล์มาวางที่นี่'}</div>
              <div className="text-[11.5px] text-slate-400 mt-0.5">ลากพร้อมกันได้หลายไฟล์ · ไม่เกิน 25 MB ต่อไฟล์</div>
            </div>
          )}
        </div>

        <p className="flex items-start gap-1.5 text-[12px] text-slate-500 bg-slate-50 rounded-xl px-3 py-2 mt-3">
          <Clock3 size={13} className="mt-[2px] flex-none text-slate-400" strokeWidth={1.9} />
          <span>
            ไฟล์ที่อัปโหลดแล้ว <b className="font-semibold text-slate-700">ลบเองได้ภายใน 24 ชั่วโมง</b> หลังจากนั้นต้องให้ผู้ดูแลระบบ (Admin) เป็นผู้ลบ
          </span>
        </p>

        {err && <p className="text-[12.5px] text-rose-600 mt-3">{err}</p>}

        {!files.length ? (
          <p className="text-[13px] text-slate-400 mt-4">ยังไม่มีเอกสาร</p>
        ) : (
          <ul className="mt-4 divide-y divide-slate-100">
            {files.map((f, i) => {
              const href = fileHref(f)
              const left = deleteWindowLeft(f, now)
              const deletable = canDeleteFile(f, me, now)
              return (
                <li key={f?.uuid || f?.location || `i${i}`} className="flex items-center gap-3 py-2.5 group">
                  <FileThumb file={f} href={href} />
                  <div className="min-w-0 flex-1">
                    {href
                      ? <a href={href} target="_blank" rel="noopener noreferrer" className="block text-[13.5px] font-medium text-brand-700 hover:underline truncate">{f?.name || `ไฟล์ ${i + 1}`}</a>
                      : <span className="block text-[13.5px] text-slate-700 truncate">{f?.name || `ไฟล์ ${i + 1}`}</span>}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[11.5px] text-slate-400">
                      {fileTagText(f) && <span className="inline-flex items-center rounded-md bg-slate-100 text-slate-500 px-1.5 py-0.5">{fileTagText(f)}</span>}
                      <span>{fmtDateTimeTH(f?.created_at)}</span>
                      {f?.size ? <span>{fmtBytes(f.size)}</span> : null}
                      {left > 0
                        ? <span className="text-amber-600">ลบได้ · {fmtWindowLeft(left)}</span>
                        : <span className="inline-flex items-center gap-1"><Lock size={10} strokeWidth={2} /> ลบได้เฉพาะ Admin</span>}
                    </div>
                  </div>
                  {deletable && (
                    <button
                      onClick={() => setPending(f)}
                      disabled={busy}
                      title={left > 0 ? 'ลบไฟล์' : 'ลบไฟล์ (สิทธิ์ Admin)'}
                      className="p-1.5 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition shrink-0 disabled:opacity-30"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </Card>

      {pending && (
        <ConfirmDialog
          title="ลบเอกสารนี้?"
          message={
            deleteWindowLeft(pending, now) > 0
              ? `"${pending?.name || 'ไฟล์นี้'}" จะถูกลบออกจากประวัติและลบไฟล์ออกจากคลังถาวร`
              : `"${pending?.name || 'ไฟล์นี้'}" เกิน 24 ชั่วโมงแล้ว — คุณกำลังลบด้วยสิทธิ์ Admin`
          }
          confirmLabel="ลบ"
          tone="danger"
          onConfirm={() => remove(pending)}
          onCancel={() => setPending(null)}
        />
      )}
      {isAdmin && files.length > 0 && (
        <p className="text-[11.5px] text-slate-400 mt-2 px-1">
          คุณมีสิทธิ์ Admin — ลบไฟล์ได้ทุกไฟล์แม้พ้น 24 ชั่วโมงแล้ว
        </p>
      )}
    </div>
  )
}

/**
 * File row preview. The stored inline `thumbnail` first; for an image without
 * one, the proxy URL `loading="lazy"` (that pulls the FULL object, hence not the
 * first choice); a document icon for everything else and for images that fail.
 */
function FileThumb({ file, href }) {
  const [failed, setFailed] = useState(false)
  const src = file?.thumbnail || (isImageFile(file) && !failed ? href : '')
  const box = 'w-10 h-10 rounded-lg shrink-0 overflow-hidden'
  if (!src || failed) {
    return <span className={`${box} bg-slate-100 text-slate-300 flex items-center justify-center`}><FileText size={15} /></span>
  }
  const img = <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className="w-full h-full object-cover" />
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" className={`${box} ring-1 ring-slate-200 hover:ring-brand-300 block`}>{img}</a>
    : <span className={`${box} ring-1 ring-slate-200`}>{img}</span>
}

/**
 * Pull the most useful sentence out of an error body.
 *
 * The order matters. `hint` and `detail` are ours and are written for the person
 * reading them; `message` is Xano's (e.g. "Missing param: email") and is at least
 * specific; `error` is a machine code and only beats a bare status. Falling
 * straight through to `HTTP 400` — which is what an earlier version did — throws
 * away the one line that says what actually went wrong.
 */
function pickMessage(j, status) {
  return j?.hint || j?.detail || j?.message || j?.error || `HTTP ${status}`
}

async function errorText(r) {
  const j = await r.json().catch(() => null)
  return pickMessage(j, r.status)
}

// apiGet/patchJson throw `HTTP 400: {"error":"…","detail":"…"}` — dig the body
// back out of the envelope so the user sees the message, not the wrapper.
function readableError(e) {
  const raw = String(e?.message || e || '')
  const m = raw.match(/\{[\s\S]*\}$/)
  if (m) {
    try { return pickMessage(JSON.parse(m[0]), '') } catch { /* fall through */ }
  }
  return raw
}
